import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { warning, error } from '@react/react-spectrum/Toast/js/ToastContainer'

import { CLIENT_PATHS } from 'config/constants'

import { RootContext } from 'root'

import A from 'services/analytics'
import { API_PROJECTS } from 'services/environment'
import {
  getConfig,
  useProjectQuery,
  updateProjectPart,
  updateProjectScreen,
  renameProjectScreen,
} from 'services/project'

import { readApolloError } from 'utils/errors'

import { useConflictWarning } from './conflicts'

const ProjectContext = createContext({
  loading: false,
  error: null,
  project: null,
  config: null,
  isObserver: false,
  isEditor: false,
  isOwner: false,
  canEdit: false,
  id: null,
  type: null,
})

export function ProjectContextProvider({ children }) {
  const { projectId, tab } = useParams()
  const { user } = useContext(RootContext)
  const { loading, error, data } = useProjectQuery(projectId)

  const project = useMemo(() => data?.project, [data])
  const type = useMemo(() => project?.meta?.type, [project])
  const config = useMemo(() => getConfig(type), [type])

  useConflictWarning({ projectId, user, tab })

  useEffect(() => {
    if (project?.meta?.id && A.getProjectID() !== project?.meta?.id) {
      A.setProject(project)
      A.triggerOpenProject()
    }
  }, [project])

  const isObserver = project?.observers?.includes(user?.id)
  const isEditor = project?.editors?.includes(user?.id)
  const isOwner = project?.owner === user?.id
  const canEdit = isOwner || isEditor
  const canDelete = isOwner

  const value = {
    loading,
    error: readApolloError(error),
    project,
    config,
    isObserver,
    isEditor,
    isOwner,
    canEdit,
    canDelete,
    id: projectId,
    type,
  }
  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
}

export function useProject() {
  const projectState = useContext(ProjectContext)

  const _updateProjectPart = useCallback(
    (partName, partContent, immediate) => {
      if (projectState.canEdit) {
        updateProjectPart(projectState.id, partName, partContent, immediate)
      } else {
        warning('You are not allowed to make changes to this project. Ask project owner for View & Edit permission.')
      }
    },
    [projectState.id, projectState.canEdit]
  )

  const _updateProjectScreen = useCallback(
    (screenName, payload) => {
      if (projectState.canEdit) {
        updateProjectScreen(projectState.id, screenName, payload)
      } else {
        warning('You are not allowed to make changes to this project. Ask project owner for View & Edit permission.')
      }
    },
    [projectState.id, projectState.canEdit]
  )

  const _renameProjectScreen = useCallback(
    async (oldScreenName, newScreenName, newScreenTitle) => {
      if (projectState.canEdit) {
        try {
          await renameProjectScreen(projectState.id, oldScreenName, newScreenName, newScreenTitle)
        } catch (e) {
          error('Error renaming screen', typeof e === 'string' ? e : e.message)
        }
      } else {
        warning('You are not allowed to make changes to this project. Ask project owner for View & Edit permission.')
      }
    },
    [projectState.id, projectState.canEdit]
  )

  return {
    updateProjectPart: _updateProjectPart,
    updateProjectScreen: _updateProjectScreen,
    renameProjectScreen: _renameProjectScreen,
    ...projectState,
  }
}

export function getPreviewLink(project) {
  const link = project?.meta?.type && (CLIENT_PATHS[project.meta.type] + project.meta.id || project.meta.url)
  return prefixLink(link)
}

export function getAliasedLink(project) {
  const link =
    project?.meta?.type && (CLIENT_PATHS[project.meta.type] + (project.alias || project.meta.id) || project.meta.url)
  return prefixLink(link)
}

export function getProxiedPreviewLink(project) {
  const link = API_PROJECTS + `/${project.alias || project.meta.id}`
  return prefixLink(link)
}

function prefixLink(previewLink) {
  if (previewLink.startsWith('http')) return previewLink
  return window.location.origin + previewLink
}
